import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Commit sa posiela po jednom riadku – tým pádom sa progress (počet importovaných)
// aktualizuje po každom produkte, nie po dávke, a zároveň každý ďalší riadok vidí
// najaktuálnejší stav DB (dôležité pre spárovanie duplicitných mien v tom istom súbore).
const COMMIT_BATCH_SIZE = 1;

const matchedViaLabel = (via, t) => {
  if (via === "customProductId") {
    return t("pages.csvImportExport.matchedViaCustomProductId", {
      defaultValue: "zhoda: customProductId",
    });
  }
  if (via === "name") {
    return t("pages.csvImportExport.matchedViaName", { defaultValue: "zhoda: názov" });
  }
  return null;
};

/**
 * Zdieľaný import dialóg (produkty aj kategórie): výber CSV súboru -> náhľad
 * (parse + validácia na backende, bez zápisu) -> potvrdenie -> commit (po dávkach,
 * s reálnym progress barom) -> výsledky.
 * Import je blokovaný, ak náhľad obsahuje čo i len jeden neplatný riadok –
 * zámerne žiadne čiastočné aplikovanie, aby stav zostal predvídateľný.
 */
const CsvImportDialog = ({ open, onClose, title, previewFn, commitFn, onImported }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [stage, setStage] = useState("select"); // select | loading | preview | committing | result
  const [previewData, setPreviewData] = useState(null);
  const [resultData, setResultData] = useState(null);
  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const [error, setError] = useState(null);
  const [stopRequested, setStopRequested] = useState(false);
  const cancelRef = useRef(false);

  const reset = () => {
    setFile(null);
    setStage("select");
    setPreviewData(null);
    setResultData(null);
    setProgress({ processed: 0, total: 0 });
    setError(null);
    setStopRequested(false);
    cancelRef.current = false;
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (e) => {
    const selected = e.target.files?.[0];
    e.target.value = "";
    if (!selected) return;
    setFile(selected);
    setError(null);
    setStage("loading");
    try {
      const data = await previewFn(selected);
      setPreviewData(data);
      setStage("preview");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          t("pages.csvImportExport.previewFailed", {
            defaultValue: "Náhľad súboru zlyhal.",
          })
      );
      setStage("select");
    }
  };

  const handleStopCommit = () => {
    cancelRef.current = true;
    setStopRequested(true);
  };

  const handleCommit = async () => {
    if (!file || !previewData) return;
    const total = previewData.totalRows;
    cancelRef.current = false;
    setStopRequested(false);
    setStage("committing");
    setError(null);
    setProgress({ processed: 0, total });

    const allResults = [];
    let appliedRows = 0;
    let failedRows = 0;
    let processedSoFar = 0;
    let wasStopped = false;
    try {
      for (let offset = 0; offset < total; offset += COMMIT_BATCH_SIZE) {
        if (cancelRef.current) {
          wasStopped = true;
          break;
        }
        const data = await commitFn(file, offset, COMMIT_BATCH_SIZE);
        allResults.push(...data.results);
        appliedRows += data.appliedRows;
        failedRows += data.failedRows;
        processedSoFar = Math.min(offset + COMMIT_BATCH_SIZE, total);
        setProgress({ processed: processedSoFar, total });
      }
      setResultData({
        totalRows: total,
        appliedRows,
        failedRows,
        results: allResults,
        stopped: wasStopped,
        stoppedAtRow: wasStopped ? processedSoFar + 2 : null,
      });
      setStage("result");
    } catch (err) {
      // Zlyhanie celej dávky (napr. výpadok siete/servera) – nie riadku, tie sa
      // zachytávajú a logujú už na backende. Aj tak hneď ukážeme, čo sa stihlo
      // spracovať a presne pri ktorom riadku import zastal, nech je jasné, kde hľadať chybu.
      const message =
        err?.response?.data?.message ||
        t("pages.csvImportExport.importFailed", { defaultValue: "Import zlyhal." });
      setError(
        t("pages.csvImportExport.importFailedAt", {
          defaultValue: `${message} Zastavené na riadku ${processedSoFar + 2} (spracovaných ${processedSoFar} z ${total}).`,
          message,
          row: processedSoFar + 2,
          processed: processedSoFar,
          total,
        })
      );
      setResultData({
        totalRows: total,
        appliedRows,
        failedRows,
        results: allResults,
        aborted: true,
        abortedAtRow: processedSoFar + 2,
      });
      setStage("result");
    }
  };

  const handleFinish = () => {
    onImported?.();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {stage === "select" && (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t("pages.csvImportExport.selectFileHelp", {
                defaultValue:
                  "Vyberte CSV súbor. Riadok sa spáruje s existujúcim záznamom podľa id, ak chýba tak podľa customProductId, a ak ani to nie je vyplnené tak podľa názvu (sk) – ak sa nenájde zhoda, vytvorí sa nový záznam.",
              })}
            </Typography>
            <Button variant="outlined" component="label">
              {t("pages.csvImportExport.selectFile", { defaultValue: "Vybrať súbor" })}
              <input type="file" accept=".csv,text/csv" hidden onChange={handleFileChange} />
            </Button>
          </Box>
        )}

        {stage === "loading" && (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        )}

        {stage === "committing" && (
          <Box py={2}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {stopRequested
                ? t("pages.csvImportExport.stopping", {
                    defaultValue: "Zastavuje sa po dokončení aktuálneho produktu…",
                  })
                : t("pages.csvImportExport.importing", { defaultValue: "Prebieha import…" })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0}
              sx={{ height: 10, borderRadius: 1, mb: 1 }}
            />
            <Typography variant="body2" align="right">
              {progress.processed} / {progress.total} (
              {progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0}
              %)
            </Typography>
          </Box>
        )}

        {stage === "preview" && previewData && (
          <Box>
            <Stack direction="row" spacing={2} mb={2}>
              <Typography variant="body2">
                {t("pages.csvImportExport.totalRows", { defaultValue: "Riadkov spolu" })}:{" "}
                <strong>{previewData.totalRows}</strong>
              </Typography>
              <Typography variant="body2" color="success.main">
                {t("pages.csvImportExport.validRows", { defaultValue: "Platných" })}:{" "}
                <strong>{previewData.validRows}</strong>
              </Typography>
              <Typography variant="body2" color="error.main">
                {t("pages.csvImportExport.invalidRows", { defaultValue: "Neplatných" })}:{" "}
                <strong>{previewData.invalidRows}</strong>
              </Typography>
            </Stack>
            {previewData.invalidRows > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("pages.csvImportExport.fixErrorsHelp", {
                  defaultValue:
                    "Súbor obsahuje neplatné riadky. Opravte chyby a nahrajte súbor znova – import je možný až keď sú všetky riadky platné.",
                })}
              </Alert>
            )}
            <List dense disablePadding sx={{ maxHeight: 320, overflowY: "auto" }}>
              {previewData.rows.map((row) => (
                <ListItem key={row.rowNumber} disableGutters alignItems="flex-start">
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    {row.valid ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={`#${row.rowNumber} ${row.name || ""} (${row.action}${
                      matchedViaLabel(row.matchedVia, t)
                        ? `, ${matchedViaLabel(row.matchedVia, t)}`
                        : ""
                    })`}
                    secondary={
                      <>
                        {row.errors.map((e, i) => (
                          <Typography key={i} variant="caption" color="error.main" display="block">
                            {e}
                          </Typography>
                        ))}
                        {row.warnings.map((w, i) => (
                          <Typography
                            key={i}
                            variant="caption"
                            color="warning.main"
                            display="block"
                          >
                            <WarningAmberIcon
                              fontSize="inherit"
                              sx={{ verticalAlign: "middle", mr: 0.5 }}
                            />
                            {w}
                          </Typography>
                        ))}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {stage === "result" && resultData && (
          <Box>
            {resultData.stopped && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("pages.csvImportExport.stoppedManually", {
                  defaultValue: `Import bol ručne zastavený na riadku ${resultData.stoppedAtRow}. Zostávajúce riadky sa nespracovali.`,
                  row: resultData.stoppedAtRow,
                })}
              </Alert>
            )}
            <Stack direction="row" spacing={2} mb={2}>
              <Typography variant="body2">
                {t("pages.csvImportExport.totalRows", { defaultValue: "Riadkov spolu" })}:{" "}
                <strong>{resultData.totalRows}</strong>
              </Typography>
              <Typography variant="body2" color="success.main">
                {t("pages.csvImportExport.appliedRows", { defaultValue: "Aplikovaných" })}:{" "}
                <strong>{resultData.appliedRows}</strong>
              </Typography>
              <Typography variant="body2" color="error.main">
                {t("pages.csvImportExport.failedRows", { defaultValue: "Zlyhaných" })}:{" "}
                <strong>{resultData.failedRows}</strong>
              </Typography>
            </Stack>
            <List dense disablePadding sx={{ maxHeight: 320, overflowY: "auto" }}>
              {resultData.results.map((row) => (
                <ListItem key={row.rowNumber} disableGutters alignItems="flex-start">
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    {row.ok ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={`#${row.rowNumber} ${
                      row.ok
                        ? row.isNew
                          ? t("pages.csvImportExport.created", { defaultValue: "vytvorené" })
                          : t("pages.csvImportExport.updated", { defaultValue: "aktualizované" })
                        : ""
                    }${
                      row.ok && matchedViaLabel(row.matchedVia, t)
                        ? ` (${matchedViaLabel(row.matchedVia, t)})`
                        : ""
                    }`}
                    secondary={row.errors?.map((e, i) => (
                      <Typography key={i} variant="caption" color="error.main" display="block">
                        {e}
                      </Typography>
                    ))}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {stage === "result" ? (
          <Button onClick={handleFinish} variant="contained">
            {t("buttons.close", { defaultValue: "Zavrieť" })}
          </Button>
        ) : stage === "committing" ? (
          <Button onClick={handleStopCommit} color="warning" disabled={stopRequested}>
            {stopRequested
              ? t("pages.csvImportExport.stopping", { defaultValue: "Zastavuje sa…" })
              : t("pages.csvImportExport.stopImport", { defaultValue: "Zastaviť import" })}
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>{t("buttons.cancel", { defaultValue: "Zrušiť" })}</Button>
            {stage === "preview" && (
              <Button
                onClick={handleCommit}
                variant="contained"
                disabled={!previewData || previewData.invalidRows > 0 || previewData.totalRows === 0}
              >
                {t("pages.csvImportExport.confirmImport", { defaultValue: "Potvrdiť import" })}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CsvImportDialog;
